import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import useApiCall from "./useApiCall";

/**
 * Custom hook to handle POST requests using useApiCall.
 *
 * @param {string} url - The API endpoint URL where the POST request will be sent.
 * @param {object} tokens - The authentication tokens for accessing the API.
 * @returns {object} - An object containing the response data, loading state,
 *  error state, and request function.
 */

const usePostRequest = (url, tokens) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { makeRequest, isLoading } = useApiCall();

  /**
   * Function to send a POST request to the given URL.
   *
   * @param {object} body - The request body to be sent with the POST request.
   * @param {string} [urlsParam=""] - Optional URL parameters to append.
   * @returns {Promise<any>} - The response data from the API.
   * @throws {Error} - Throws an error if the POST request fails.
   */

  const sendPostRequest = useCallback(
    async (body, urlsParam = "") => {
      setError(null);

      try {
        const response = await makeRequest(url + urlsParam, {
          method: "POST",
          authTokens: tokens,
          body,
        });

        setData(response || []);
        toast.success(response?.message || "Request successful");
        return response;
      } catch (err) {
        setError(err);
        throw err;
      }
    },
    [url, tokens, makeRequest]
  );

  return { data, loading: isLoading, error, sendPostRequest };
};

export default usePostRequest;
